import React from 'react';
import { useAuthStore } from '../../../context/AuthContext';
import { useTankCleaningStore } from '../../../context/TankCleaningContext';
import { ChemicalOrderStatusEnum } from '../../../enums/chemicalOrderStatusEnum';
import { RoleTypeEnum } from '../../../enums/roleTypeEnum';
import { PortCall } from '../../../types/ViewModel/PortCall';
import { Chemical } from '../../../types/ViewModel/TankCleaning/Chemical';
import { ChemicalOrderItem } from '../../../types/ViewModel/TankCleaning/Order/ChemicalOrderItem';
import OdfjellInputComponent, { InputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import { ChemicalCategoryInputsWrapper, ChemicalOrderItemRow, ListValuesWrapper, OrderEditValue } from './ChemicalOrder.styles';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';

type Props = {
    chemicalOrderItems: ChemicalOrderItem[];
    previousPortCallWithRob?: PortCall;
};

const ChemicalCategoryOrderItemsComponent: React.FC<Props> = (props) => {
    const { chemicalOrderUpdateReportData, setChemicalOrderUpdateReportData } = useTankCleaningStore();
    const { role, loggedInUser } = useAuthStore();
    const { checkIsTankCleaningReadOnly } = useReadOnlyUtils();

    // =============  Use Effects ================

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;
        const selectedChemicalOrderItem: ChemicalOrderItem = changeObject.dataObject;

        updateReports(selectedChemicalOrderItem, name, value);
    };

    const updateReports = (selectedChemicalOrderItem: ChemicalOrderItem, name: string, value: string) => {
        const selectedChemical: Chemical | null | undefined = selectedChemicalOrderItem.chemical;

        if (name === 'comment') {
            selectedChemicalOrderItem[name] = value;
        } else {
            selectedChemicalOrderItem[name] = Number(value);
        }

        if (chemicalOrderUpdateReportData?.chemicalOrderItemList && chemicalOrderUpdateReportData?.chemicalOrderItemList.length > 0) {
            const chemicalOrderItem = chemicalOrderUpdateReportData?.chemicalOrderItemList.find((o) => o.chemicalId === selectedChemical?.chemicalId);
            if (chemicalOrderItem && selectedChemical) {
                chemicalOrderItem.chemicalId = selectedChemical.chemicalId;
                chemicalOrderItem.chemical = selectedChemical;

                if (name === 'comment') {
                    chemicalOrderItem[name] = value;
                } else {
                    chemicalOrderItem[name] = Number(value);
                }

                chemicalOrderItem.robValue = getPreviousRobValue(selectedChemicalOrderItem);
            }
        }

        if (chemicalOrderUpdateReportData) {
            setChemicalOrderUpdateReportData({ ...chemicalOrderUpdateReportData });
        }
    };

    const isFieldReadOnly = () => {
        if (
            chemicalOrderUpdateReportData?.lastOrderStatusId === ChemicalOrderStatusEnum.Canceled ||
            chemicalOrderUpdateReportData?.lastOrderStatusId === ChemicalOrderStatusEnum.Confirmed
        ) {
            return true;
        } else if (
            role.roleType === RoleTypeEnum.ShipUser &&
            chemicalOrderUpdateReportData?.lastOrderStatusId !== ChemicalOrderStatusEnum.Draft &&
            chemicalOrderUpdateReportData?.lastOrderStatusId !== ChemicalOrderStatusEnum.Unsaved
        ) {
            return true;
        } else if (
            role.roleType === RoleTypeEnum.ShipOperator &&
            chemicalOrderUpdateReportData?.createdBy !== loggedInUser?.userName &&
            chemicalOrderUpdateReportData?.lastOrderStatusId === ChemicalOrderStatusEnum.Draft
        ) {
            return true;
        } else {
            return false;
        }
    };

    const getPreviousRobValue = (chemicalOrderItem: ChemicalOrderItem) => {
        const chemicalRobForPortCall = props.previousPortCallWithRob?.chemicalRobForPortCall;
        if (chemicalRobForPortCall) {
            const previousRobItem = chemicalRobForPortCall.chemicalRobItemList.find((c) => c.chemicalId === chemicalOrderItem.chemicalId);
            if (previousRobItem) {
                return previousRobItem.robValue;
            }
        }
        return 0;
    };

    const hasPreviousOrderValue = (chemicalOrderItem: ChemicalOrderItem): boolean => {
        if (chemicalOrderItem.previousOrderValue && chemicalOrderItem.previousOrderValue > 0) {
            return true;
        } else {
            return false;
        }
    };

    const isReadOnly = checkIsTankCleaningReadOnly();

    return (
        <ChemicalCategoryInputsWrapper>
            {props.chemicalOrderItems && props.chemicalOrderItems?.length > 0 && (
                <ul>
                    {props.chemicalOrderItems.map((chemicalOrderItem, index) => {
                        return (
                            <li key={`orderItem_${chemicalOrderUpdateReportData?.portCallId}_${chemicalOrderItem.chemicalId}_${index}`}>
                                <ListValuesWrapper gridCols={4}>
                                    <ChemicalOrderItemRow>
                                        <span className={'category-col'}>{chemicalOrderItem.chemical?.name}</span>
                                        <OdfjellInputComponent
                                            type={'number'}
                                            isReadOnly={true}
                                            value={getPreviousRobValue(chemicalOrderItem)}
                                            name={'previousRobValue'}
                                            unit={chemicalOrderItem.chemical?.unit?.unitName ?? ''}
                                        />

                                        {!isFieldReadOnly() && (
                                            <>
                                                <OrderEditValue className="current-order">
                                                    {hasPreviousOrderValue(chemicalOrderItem) && (
                                                        <OdfjellInputComponent
                                                            type={'number'}
                                                            isReadOnly={true}
                                                            value={chemicalOrderItem.previousOrderValue}
                                                            name={'previousOrderValue'}
                                                            unit={chemicalOrderItem.chemical?.unit?.unitName ?? ''}
                                                        />
                                                    )}
                                                    <OdfjellInputComponent
                                                        dataObject={chemicalOrderItem}
                                                        extraArgs={1}
                                                        isReadOnly={isReadOnly}
                                                        type={'number'}
                                                        value={chemicalOrderItem?.orderValue}
                                                        name={'orderValue'}
                                                        onChange={onInputChange}
                                                        unit={chemicalOrderItem.chemical?.unit?.unitName ?? ''}
                                                    />
                                                </OrderEditValue>
                                                <OdfjellInputComponent
                                                    dataObject={chemicalOrderItem}
                                                    extraArgs={1}
                                                    isReadOnly={isReadOnly}
                                                    type={'text'}
                                                    value={chemicalOrderItem?.comment}
                                                    name={'comment'}
                                                    onChange={onInputChange}
                                                />
                                            </>
                                        )}
                                        {isFieldReadOnly() && (
                                            <>
                                                <OrderEditValue>
                                                    {hasPreviousOrderValue(chemicalOrderItem) && (
                                                        <OdfjellInputComponent
                                                            type={'number'}
                                                            isReadOnly={true}
                                                            value={chemicalOrderItem.previousOrderValue}
                                                            name={'previousOrderValue'}
                                                            unit={chemicalOrderItem.chemical?.unit?.unitName ?? ''}
                                                        />
                                                    )}
                                                    <OdfjellInputComponent
                                                        dataObject={chemicalOrderItem}
                                                        extraArgs={1}
                                                        type={'number'}
                                                        isReadOnly={true}
                                                        value={chemicalOrderItem?.orderValue}
                                                        name={'orderValue'}
                                                        onChange={onInputChange}
                                                        unit={chemicalOrderItem.chemical?.unit?.unitName ?? ''}
                                                    />
                                                </OrderEditValue>
                                                <OdfjellInputComponent
                                                    dataObject={chemicalOrderItem}
                                                    extraArgs={1}
                                                    type={'text'}
                                                    isReadOnly={true}
                                                    value={chemicalOrderItem?.comment}
                                                    name={'comment'}
                                                    onChange={onInputChange}
                                                />
                                            </>
                                        )}
                                    </ChemicalOrderItemRow>
                                </ListValuesWrapper>
                            </li>
                        );
                    })}
                </ul>
            )}
        </ChemicalCategoryInputsWrapper>
    );
};

export default ChemicalCategoryOrderItemsComponent;
